import { EXPECTED_RETURN_CHECKS, EXPECTED_RETURN_STATUSES, type ExpectedReturnsQuery } from '@chukua/common';
import { Tag, type TableColumnsType } from 'antd';
import { Link } from 'react-router';
import type { ListQueryControls } from '../../../shared/hooks/use-list-query';
import { returnStatusTag } from '../policies/return-status.policy';
import { receiveTruckPath } from '../yard.paths';
import type { ExpectedReturnItem } from '../yard.api';

const CHECK_LABELS = { recorded: 'Check in', not_recorded: 'No check yet' } as const;

/** Sorting and filtering run on the server: each column's `key` is its `sort` field or filter param in `expectedReturnsQuerySchema`. */
export function buildExpectedReturnColumns(
  list: Pick<ListQueryControls<ExpectedReturnsQuery>, 'sortOrderFor' | 'filteredValueFor'>,
): TableColumnsType<ExpectedReturnItem> {
  return [
    {
      title: 'Truck',
      key: 'number',
      sorter: true,
      sortOrder: list.sortOrderFor('number'),
      render: (_, trip) => (
        <Link to={receiveTruckPath(trip.id)} className="flex flex-col py-1">
          <span className="text-base font-semibold">{trip.truckRegistration ?? 'No truck'}</span>
          <span className="text-xs text-muted">
            {trip.number} · {trip.driverName ?? 'No driver'}
          </span>
        </Link>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      filters: EXPECTED_RETURN_STATUSES.map((status) => ({ text: returnStatusTag(status).label, value: status })),
      filteredValue: list.filteredValueFor('status'),
      render: (_, trip) => {
        const tag = returnStatusTag(trip.status);
        return <Tag color={tag.color}>{tag.label}</Tag>;
      },
    },
    {
      title: 'Post-trip check',
      key: 'check',
      filters: EXPECTED_RETURN_CHECKS.map((check) => ({ text: CHECK_LABELS[check], value: check })),
      filteredValue: list.filteredValueFor('check'),
      render: (_, trip) => (
        <Tag color={trip.hasPostTripCheck ? 'success' : 'default'}>{trip.hasPostTripCheck ? CHECK_LABELS.recorded : CHECK_LABELS.not_recorded}</Tag>
      ),
    },
    {
      title: 'Delivered',
      key: 'deliveredAt',
      sorter: true,
      sortOrder: list.sortOrderFor('deliveredAt'),
      render: (_, trip) => (trip.deliveredAt ? new Date(trip.deliveredAt).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : 'Not yet'),
    },
  ];
}
